import { useEffect, useState } from 'react'
import { usePrefersReducedMotion } from '@mc/hooks'
import {
  CLIENT_APPLICATION_NAMES,
  TRACK_CONTENT_TYPES,
  TRACK_PROPS,
} from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { Video, useVideo } from '@mc/video'

// Playback-only delay, so the poster reads as a still image before the clip
// starts. <Video> mounts immediately: mounting it late made the player pop in
// over a settled hero, which is visible. Matches HeroVideo's 2s precedent.
//
// Consequence worth being explicit about: mounting is what starts the fetch
// (useLoadVideoById queries Edge Media on mount, and hls.js buffers without
// waiting for play() since autoStartLoad defaults true), so the manifest and
// first segments do load alongside the hero's `priority` Image rather than
// after it. Deliberate — a late-appearing player is worse than the bytes.
const PLAY_DELAY_MS = 2000

// Own loop + play/pause driver, instead of the library's `loop` prop /
// VideoAutoplay / VideoExternalPlayState: this is always an autoplay-only
// background loop (never user-initiated), and needs an explicit pause while
// the full trailer modal is open — neither `VideoAutoplay` nor the `loop`
// prop's restart() cover that without also being wired for the general case.
interface HeroLoopPlaybackProps {
  paused: boolean
  startTime?: number
  endTime?: number
}

const HeroLoopPlayback = ({
  paused,
  startTime,
  endTime,
}: HeroLoopPlaybackProps) => {
  const { play, pause, seekTo, readyState, state, time, duration } = useVideo()
  const [delayElapsed, setDelayElapsed] = useState(false)

  const start = startTime ?? 0
  const hasEnd = endTime != null && endTime > start
  const startInRange = duration <= 0 || start < duration
  const [seekedToStart, setSeekedToStart] = useState(start === 0)

  useEffect(() => {
    const timeout = setTimeout(() => setDelayElapsed(true), PLAY_DELAY_MS)
    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    if (start === 0 || seekedToStart || !startInRange) return
    if (readyState !== 'ready' || !delayElapsed) return

    seekTo(start)
      .catch(() => {})
      .finally(() => setSeekedToStart(true))
  }, [start, seekedToStart, startInRange, readyState, delayElapsed, seekTo])

  useEffect(() => {
    if (readyState !== 'ready') return

    if (paused) {
      if (state === 'playing') pause()
      return
    }

    if (!delayElapsed || (startInRange && !seekedToStart)) return

    // When the video ends with a non-zero start within the media's duration,
    // seek back to start. The seek flips state to 'paused' via the reducer
    // (emitting a PAUSED playback event — accepted, as it's the only way to
    // restart at a non-zero offset), which re-triggers this effect and reaches
    // the play() call below. When start is 0, skip the seek — play() on an
    // ended element rewinds by itself (HTML spec, "play the media element"
    // step 2).
    if (state === 'ended' && start > 0 && startInRange) {
      seekTo(start)
      return
    }

    if (state !== 'playing') play({ autoPlay: true }).catch(() => {})
  }, [
    paused,
    delayElapsed,
    seekedToStart,
    startInRange,
    readyState,
    state,
    start,
    play,
    pause,
    seekTo,
  ])

  useEffect(() => {
    if (!hasEnd || state !== 'playing' || paused) return
    if (time >= endTime) {
      seekTo(start)
    }
  }, [time, endTime, start, hasEnd, state, paused, seekTo])

  return null
}

export interface HeroTrailerLoopProps {
  mediaUuid: string
  posterUrl?: string
  // True while the full trailer modal is open, so the background loop doesn't
  // keep streaming (and firing playback events) behind it.
  paused?: boolean
  startTime?: number
  endTime?: number
}

export const HeroTrailerLoop = ({
  mediaUuid,
  posterUrl,
  paused = false,
  startTime,
  endTime,
}: HeroTrailerLoopProps) => {
  const prefersReducedMotion = usePrefersReducedMotion()

  if (prefersReducedMotion) return null

  return (
    <div
      className='pointer-events-none absolute inset-0 overflow-hidden rounded-xl'
      aria-hidden
    >
      <TrackingParamsProvider
        value={{
          [TRACK_PROPS.CONTENT_ID]: mediaUuid,
          [TRACK_PROPS.CONTENT_TYPE]:
            TRACK_CONTENT_TYPES.CERTIFICATE_CLP_HERO_AUTO_PLAY,
          [TRACK_PROPS.VIEW_CLIENT_APPLICATION_NAME]:
            CLIENT_APPLICATION_NAMES.CERTS,
        }}
      >
        {/* No VideoContainer/controls — PRD requires no audio, no fullscreen.
            `muted` is on the underlying <video> element from first paint, so
            play() below never hits the browser's unmuted-autoplay block. */}
        <Video
          className='h-full'
          aspectRatio='1x1'
          mediaUuid={mediaUuid}
          exemptSingleton
          playsInline
          muted
          fit='cover'
          poster={posterUrl}
        >
          <HeroLoopPlayback
            paused={paused}
            startTime={startTime}
            endTime={endTime}
          />
        </Video>
      </TrackingParamsProvider>
    </div>
  )
}
